// @mui
import { ListItemButton, ListItemIcon, ListItemText, ListSubheader } from "@mui/material"
import { styled } from "@mui/material/styles"
import { interactiveStateStyles } from "lib/@components/interactive-state-styles"
// utils
import cssStyles from "minimals-template/components/utils/cssStyles"
// config
import { ICON, NAVBAR } from "config"

// ----------------------------------------------------------------------

export const ListItemStyle = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== "active" && prop !== "isCollapse",
})(({ active, depth, highlight, isCollapse, theme }) => ({
    position: "relative",
    textTransform: "capitalize",
    width: "100%",
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(1.5),
    marginBottom: `2px !important`,
    color: theme.palette.text.primary,
    fontWeight: 500,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    minHeight: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
    ...interactiveStateStyles(theme),
    // Active item
    ...(active && {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.state.selected,
        "&:hover": {
            backgroundColor: theme.palette.state.selectedHover,
        },
    }),
    // Active item
    ...(active &&
        depth !== 1 &&
        !highlight && {
            color: theme.palette.text.primary,
            backgroundColor: "transparent",
        }),
    // Sub item
    ...(depth && {
        ...(depth > 1 && {
            height: "auto",
        }),
        ...(depth > 2 && {
            paddingLeft: theme.spacing(depth),
        }),
    }),
    ...(isCollapse && {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        justifyContent: "center",
    }),
}))

// ----------------------------------------------------------------------

export const ListItemTextStyle = styled(ListItemText, {
    shouldForwardProp: (prop) => prop !== "isCollapse",
})(({ isCollapse, theme }) => ({
    color: theme.palette.text.primary,
    "& .MuiListItemText-primary": {
        fontSize: 14,
        lineHeight: "22px",
        fontWeight: 400,
    },
    transition: theme.transitions.create(["width", "opacity"], {
        duration: theme.transitions.duration.shorter,
    }),
    ...(isCollapse && {
        width: 0,
        opacity: 0,
        flexGrow: 0,
        margin: 0,
    }),
}))

// ----------------------------------------------------------------------

export const ListItemIconStyle = styled(ListItemIcon, {
    shouldForwardProp: (prop) => prop !== "isCollapse",
})(({ isCollapse, theme }) => ({
    width: ICON.NAVBAR_ITEM,
    height: ICON.NAVBAR_ITEM,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    "& svg": { width: "100%", height: "100%" },
    // Theme gives ListItemIcon a 16px marginRight to space it from the label —
    // there is no label in the collapsed rail, so it just skews the centring
    ...(isCollapse && { marginRight: 0 }),
}))

// ----------------------------------------------------------------------

export const ListSubheaderStyle = styled(ListSubheader)(({ theme }) => ({
    ...theme.typography.overline,
    borderRadius: theme.shape.borderRadius,
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    transition: theme.transitions.create("opacity", {
        duration: theme.transitions.duration.shorter,
    }),
    ...cssStyles(theme).bgBlur(),
}))

// ----------------------------------------------------------------------

export const GroupHeaderStyle = styled("summary", {
    shouldForwardProp: (prop) => prop !== "isCollapse",
})(({ isCollapse, theme }) => ({
    ...theme.typography.overline,
    fontSize: 12,
    lineHeight: "18px",
    fontWeight: 700,
    letterSpacing: 0,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    textAlign: "left",
    textTransform: "uppercase",
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1.5),
    paddingLeft: 0,
    paddingRight: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    cursor: "pointer",
    listStyle: "none",
    "&::-webkit-details-marker": { display: "none" },
    "&::marker": { content: '""' },
    transition: theme.transitions.create("opacity", {
        duration: theme.transitions.duration.shorter,
    }),
    ...interactiveStateStyles(theme),
    ...cssStyles(theme).bgBlur(),
    ...(isCollapse && { display: "none" }),
}))
